import React from 'react'
import { ChatInput, ChatMessageUpgrade } from '@/components'
import { InteractiveComponent } from './InteractiveComponents'
import BotMessageUser from './BotMessageUser'
import BotMessageAssistant from './BotMessageAssistant'
import BotMessageToolForm from './BotMessageToolForm'
import BotMessageToolGenerateCode from './BotMessageToolGenerateCode'
import BotMessageToolBacktest from './BotMessageToolBacktest'
import BotMessageToolOptimizer from './BotMessageToolOptimizer'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'upgrade'
  content: string
  timestamp: Date
  status?: MessageStatus
  streaming?: boolean
  messageType?: 'content' | 'tool'
  interactiveComponent?: InteractiveComponent
  toolData?: {
    toolName: string
    toolInput: Record<string, unknown>
    startedAt: string
    completedAt?: string
    status: 'running' | 'completed'
    componentType?: string
    componentData?: Record<string, unknown>
    responses?: Record<string, string | string[]>
    toolStatus?: string
    config?: Record<string, any>
    btid?: string
    cid?: string
    optid?: string
    version?: number
    codeVersion?: number
    codeId?: number
    error?: string
  }
  error?: string
}

interface BotChatProps {
  messages: Message[]
  inputValue: string
  onInputChange: (value: string) => void
  onSendMessage: () => void
  onComponentSubmit: (msgId: string, data: Record<string, unknown>) => void
  onCancelMessage?: () => void
  onRetryMessage?: () => void
  onUpgradeClick?: () => void
  isLoading?: boolean
  hideHeader?: boolean
  isMobileLayout?: boolean
  inputTrailingSlot?: React.ReactNode
}

export default function BotChat({
  messages,
  inputValue,
  onInputChange,
  onSendMessage,
  onComponentSubmit,
  onCancelMessage,
  onRetryMessage,
  onUpgradeClick,
  isLoading = false,
  hideHeader = false,
  isMobileLayout = false,
  inputTrailingSlot,
}: BotChatProps) {
  const messagesContainerRef = React.useRef<HTMLDivElement>(null)
  const lastUserMessageRef = React.useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = React.useRef(0)
  const isActiveChatRef = React.useRef(false)
  const hasScrolledOnLoadRef = React.useRef(false)
  const [showScrollButton, setShowScrollButton] = React.useState(false)

  // Check if there's any in_progress message (blocks input)
  const hasInProgressMessage = React.useMemo(() => {
    return messages.some(m => m.status === MESSAGE_STATUS.IN_PROGRESS)
  }, [messages])

  // Check if there's a failed message (blocks input until resolved)
  const failedMessage = React.useMemo(() => {
    return messages.find(m => m.status === MESSAGE_STATUS.FAILED)
  }, [messages])

  // Check if there's an in_progress form (blocks text input)
  const hasInProgressForm = React.useMemo(() => {
    return messages.some(m => 
      m.interactiveComponent && 
      !m.interactiveComponent.answered && 
      m.status === MESSAGE_STATUS.IN_PROGRESS
    )
  }, [messages])

  // Check if there's an active tool call (to suppress assistant spinner)
  const hasActiveToolCall = React.useMemo(() => {
    return messages.some(m => m.messageType === 'tool' && m.toolData && m.toolData.status === 'running')
  }, [messages])

  // Input should be disabled if: loading, in_progress message, failed message, or in_progress form
  const isInputDisabled = isLoading || hasInProgressMessage || !!failedMessage || hasInProgressForm

  // Scroll to bottom on initial load if there are messages
  React.useEffect(() => {
    if (!hasScrolledOnLoadRef.current && messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      hasScrolledOnLoadRef.current = true
    }
  }, [messages])

  // Track when we're in an active chat session (user has sent a message)
  React.useEffect(() => {
    if (isLoading) {
      isActiveChatRef.current = true
    }
  }, [isLoading])

  // Scroll when a new user message is added
  React.useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current) {
      const lastMessage = messages[messages.length - 1]
      if (lastMessage?.role === 'user') {
        isActiveChatRef.current = true
        
        if (lastUserMessageRef.current && messagesContainerRef.current) {
          requestAnimationFrame(() => {
            const container = messagesContainerRef.current
            const messageElement = lastUserMessageRef.current
            
            if (container && messageElement) {
              const containerRect = container.getBoundingClientRect()
              const messageRect = messageElement.getBoundingClientRect()
              const scrollOffset = messageRect.top - containerRect.top + container.scrollTop
              
              container.scrollTo({
                top: scrollOffset,
                behavior: 'smooth'
              })
            }
          })
        }
      }
    }
    prevMessagesLengthRef.current = messages.length
  }, [messages])

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
      setShowScrollButton(!isAtBottom)
    }
  }

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  const placeholder = failedMessage
    ? 'Resolve the error above to continue...'
    : hasInProgressForm
      ? 'Complete the form above to continue...'
      : hasInProgressMessage
        ? 'Waiting for response...'
        : 'Type a message...'

  const primaryAction: 'send' | 'stop' =
    hasInProgressMessage && onCancelMessage ? 'stop' : 'send'

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-background">
      {!hideHeader && (
        <div className="shrink-0 h-11 flex items-center px-4 border-b border-border bg-card">
          <div className="flex items-center gap-1.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-foreground shrink-0"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p className="text-label text-foreground">Chat</p>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 relative">
        <div
          ref={messagesContainerRef}
          className="absolute inset-0 overflow-y-auto overflow-x-hidden p-4"
          onScroll={handleScroll}
        >
          {messages.length === 0 ? (
            <div className="text-center mt-8 text-muted-foreground">
              Start a conversation about your strategy
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => {
                const isLastUserMessage =
                  message.role === 'user' && index === messages.length - 1

                return (
                  <div
                    key={message.id}
                    ref={isLastUserMessage ? lastUserMessageRef : null}
                    style={{
                      ...(message.role === 'assistant' &&
                      isActiveChatRef.current &&
                      (message.streaming ||
                        message.status === MESSAGE_STATUS.IN_PROGRESS ||
                        index === messages.length - 1)
                        ? { minHeight: 'calc(-248px + 100dvh)' }
                        : {}),
                    }}
                  >
                    {message.role === 'upgrade' ? (
                      <ChatMessageUpgrade
                        message={{ id: 0, type: 'upgrade', content: message.content }}
                        ctaLabel="Check credit usage"
                        onUpgrade={onUpgradeClick}
                      />
                    ) : message.role === 'user' ? (
                      <BotMessageUser
                        content={message.content}
                        status={message.status}
                      />
                    ) : message.role === 'assistant' ? (
                      message.messageType === 'tool' || message.toolData ? (
                        message.toolData?.toolName === 'ask_clarifying_questions' ? (
                          <BotMessageToolForm
                            toolData={message.toolData}
                            status={message.status}
                            msgId={message.id}
                            onSubmit={onComponentSubmit}
                          />
                        ) : message.toolData?.toolName === 'generate_trading_code' ? (
                          <BotMessageToolGenerateCode
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : message.toolData?.toolName === 'run_backtest' ? (
                          <BotMessageToolBacktest
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : message.toolData?.toolName === 'run_optimizer' ? (
                          <BotMessageToolOptimizer
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : null
                      ) : (
                        <BotMessageAssistant
                          content={message.content}
                          status={message.status}
                          streaming={message.streaming}
                          error={message.error}
                          onRetry={onRetryMessage}
                          onCancel={onCancelMessage}
                          hasActiveToolCall={hasActiveToolCall}
                        />
                      )
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {showScrollButton && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 px-3 py-1.5 rounded-full text-caption bg-card text-foreground border border-border shadow-md"
            aria-label="Scroll to bottom"
          >
            ↓
          </button>
        )}
      </div>

      <ChatInput
        value={inputValue}
        onChange={onInputChange}
        onSubmit={() => onSendMessage()}
        onStop={onCancelMessage ? () => onCancelMessage() : undefined}
        primaryAction={primaryAction}
        disabled={isInputDisabled && primaryAction !== 'stop'}
        placeholder={placeholder}
        isMobileLayout={isMobileLayout}
        trailingChromeSlot={inputTrailingSlot}
      />
    </div>
  )
}

export type { BotChatProps }
